import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchProductBySlug, fetchProducts, getProductGallery, type ProductVariant } from "@/lib/products";
import { useCart, formatPrice } from "@/lib/cart";
import { analytics } from "@/lib/analytics";
import { useWishlist } from "@/lib/wishlist";
import { ProductCard } from "@/components/site/ProductCard";
import { ProductReviews } from "@/components/site/ProductReviews";
import {
  Check,
  Plus,
  Minus,
  Star,
  ShoppingBag,
  Truck,
  RotateCcw,
  
  ShieldCheck,
  ChevronDown,
  Heart,
  Ruler,
  Hand,
  Leaf,
  
} from "lucide-react";
import leatherCloseup from "@/assets/leather-closeup.jpg";
import editorialCouple from "@/assets/editorial-couple.jpg";
import { productJsonLd, breadcrumbJsonLd, SITE_URL } from "@/lib/seo";
import { getProductRatings } from "@/lib/reviews.functions";

export const Route = createFileRoute("/product/$slug")({
  loader: async ({ params }) => {
    const product = await fetchProductBySlug(params.slug);
    if (!product) throw notFound();
    const ratingMap = await getProductRatings({ data: { slugs: [params.slug] } });
    const rating = ratingMap[params.slug];
    return { product, rating };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.product;
    if (!p) return { meta: [{ title: "Product — KAKAIYS" }] };
    const url = `${SITE_URL}/product/${p.slug}`;
    const desc = `${p.tagline} ${p.description}`.slice(0, 160);
    return {
      meta: [
        { title: `${p.name} — KAKAIYS` },
        { name: "description", content: desc },
        { property: "og:title", content: `${p.name} — KAKAIYS` },
        { property: "og:description", content: p.tagline },
        { property: "og:image", content: p.image },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${p.name} — KAKAIYS` },
        { name: "twitter:description", content: p.tagline },
        { name: "twitter:image", content: p.image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: productJsonLd({
            slug: p.slug,
            name: p.name,
            description: desc,
            image: p.image,
            price: p.price,
            currency: "USD",
            averageRating: loaderData?.rating?.avg,
            reviewCount: loaderData?.rating?.count,
          }),
        },
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", url: "/" },
            { name: "Shop", url: "/shop" },
            { name: p.category, url: `/${p.category.toLowerCase()}` },
            { name: p.name, url: `/product/${p.slug}` },
          ]),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-luxe pt-40 text-center">
      <div className="eyebrow mb-3">Not found</div>
      <h1 className="font-serif text-5xl">This piece could not be found.</h1>
      <Link
        to="/shop"
        className="mt-8 inline-flex text-xs uppercase tracking-[0.24em] border-b border-foreground pb-1"
      >
        Back to the Collection
      </Link>
    </div>
  ),
  component: ProductPage,
});

// Fallback color options for products without variants seeded yet (non-jackets).
const FALLBACK_COLOR_OPTIONS = [
  { color_name: "Onyx", color_hex: "#0a0a0a" },
  { color_name: "Cognac", color_hex: "#8b4a2b" },
  { color_name: "Chocolate", color_hex: "#3b2418" },
];

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"] as const;

function Stars({ rating = 5 }: { rating?: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < rating ? "fill-gold text-gold" : "text-muted-foreground"}`}
        />
      ))}
    </div>
  );
}

function Accordion({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-5 text-left"
        aria-expanded={open}
      >
        <span className="text-[11px] uppercase tracking-[0.28em]">{title}</span>
        <ChevronDown
          className={`h-4 w-4 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden text-sm leading-relaxed text-foreground/75">{children}</div>
      </div>
    </div>
  );
}

function ProductPage() {
  const { product } = Route.useLoaderData();
  const { add } = useCart();
  const { isWished, toggle } = useWishlist();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  // Real DB variants take priority. If none, fall back to display-only swatches.
  const variants: ProductVariant[] = product.variants ?? [];
  const hasVariants = variants.length > 0;
  const defaultVariant = variants.find((v) => v.is_default) ?? variants[0];
  const initialColor = defaultVariant?.color_name ?? FALLBACK_COLOR_OPTIONS[0].color_name;
  const [selectedColor, setSelectedColor] = useState<string>(initialColor);
  const [selectedSize, setSelectedSize] = useState<string>("M");

  const baseGallery = useMemo(() => getProductGallery(product), [product]);
  const activeVariant = hasVariants
    ? variants.find((v) => v.color_name === selectedColor) ?? defaultVariant
    : null;

  // When a non-default variant is selected, the gallery becomes that variant's single image.
  // When the default variant is selected, show the full original gallery.
  const gallery = useMemo(() => {
    if (!activeVariant) return baseGallery;
    if (activeVariant.is_default) return baseGallery;
    return [activeVariant.image_url];
  }, [activeVariant, baseGallery]);

  const [selectedImage, setSelectedImage] = useState(gallery[0] ?? product.image);

  // Reset image when product changes
  useEffect(() => {
    setSelectedColor(initialColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.slug]);

  // When the gallery changes (because color changed), reset the main image to the first one
  useEffect(() => {
    setSelectedImage(gallery[0] ?? product.image);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor, product.slug]);

  // GA4: view_item
  useEffect(() => {
    analytics.viewItem({
      slug: product.slug,
      name: product.name,
      price: product.price,
      category: product.category,
    });
  }, [product.slug, product.name, product.price, product.category]);


  const { data: allProducts = [] } = useQuery({
    queryKey: ["products", "active"],
    queryFn: () => fetchProducts(),
  });
  const related = allProducts
    .filter((p) => p.category === product.category && p.slug !== product.slug)
    .slice(0, 3);

  const onAdd = () => {
    add(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };

  const seed = product.slug.split("").reduce((s: number, c: string) => s + c.charCodeAt(0), 0);
  const sku = `KKY-${(seed * 37).toString(36).toUpperCase().slice(0, 6)}`;
  const wished = isWished(product.slug);

  // Real review aggregate for this product (no fake counts).
  const { data: ratingMap = {} } = useQuery({
    queryKey: ["product-ratings", [product.slug]],
    queryFn: () => getProductRatings({ data: { slugs: [product.slug] } }),
    staleTime: 5 * 60 * 1000,
  });
  const rating = ratingMap[product.slug];

  return (
    <>
      <div className="pt-20 md:pt-24">
        <div className="container-luxe pt-6 pb-4">
          <nav className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground flex items-center gap-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-foreground transition-colors">Shop</Link>
            <span>/</span>
            <span className="text-foreground">{product.name}</span>
          </nav>
        </div>

        <div className="grid lg:grid-cols-[1.25fr_1fr] bg-bone">
          <div className="bg-cream">
            <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden">
              <img
                src={selectedImage}
                alt={product.name}
                loading="eager"
                width={1400}
                height={1680}
                className="h-full w-full object-cover"
              />
              {product.tags?.includes("new-arrivals") && (
                <span className="absolute top-6 left-6 z-10 bg-bone/95 text-ink text-[10px] uppercase tracking-[0.32em] px-4 py-2">
                  New Arrival
                </span>
              )}
            </div>
            <div className="px-6 md:px-10 py-6 md:py-8">
              <div className="flex gap-3 md:gap-4 overflow-x-auto pb-1 scrollbar-hide">
                {gallery.map((src, i) => {
                  const active = src === selectedImage;
                  return (
                    <button
                      key={`${src}-${i}`}
                      type="button"
                      onClick={() => setSelectedImage(src)}
                      className={`relative shrink-0 w-20 h-24 md:w-24 md:h-28 overflow-hidden transition-all ${
                        active ? "ring-1 ring-ink" : "opacity-70 hover:opacity-100"
                      }`}
                      aria-label={`View image ${i + 1} of ${product.name}`}
                    >
                      <img
                        src={src}
                        alt={`${product.name} thumbnail ${i + 1}`}
                        loading="lazy"
                        width={192}
                        height={240}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto">
            <div className="px-6 py-14 md:px-16 md:py-20 max-w-xl mx-auto w-full">
              <div className="eyebrow mb-4">{product.category}</div>
              <h1 className="font-serif font-light text-3xl md:text-5xl leading-[1.1] tracking-[-0.01em] text-ink">
                {product.name}
              </h1>

              {rating && rating.count > 0 && (
                <div className="mt-5 flex items-center gap-3 text-xs text-stone">
                  <Stars rating={Math.round(rating.avg)} />
                  <Link to="/reviews" className="hover:text-ink transition-colors">
                    ({rating.count.toLocaleString()} {rating.count === 1 ? "review" : "reviews"})
                  </Link>
                </div>
              )}

              <div className="mt-7 text-xl md:text-2xl font-serif tabular-nums text-ink font-light">
                {formatPrice(product.price)}
              </div>

              <p className="mt-8 text-sm text-ash italic font-serif leading-relaxed">
                {product.tagline}
              </p>

              <div className="hairline my-7" />

              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <span className="eyebrow">Colour</span>
                  <span className="text-xs text-foreground/80">{selectedColor}</span>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {hasVariants
                    ? variants.map((v) => {
                        const active = selectedColor === v.color_name;
                        return (
                          <button
                            key={v.id}
                            type="button"
                            aria-label={v.color_name}
                            title={v.color_name}
                            onClick={() => setSelectedColor(v.color_name)}
                            style={{ backgroundColor: v.color_hex }}
                            className={`h-8 w-8 rounded-full border border-bone/30 transition-all ${
                              active
                                ? "ring-2 ring-gold ring-offset-2 ring-offset-background"
                                : "hover:scale-110"
                            }`}
                          />
                        );
                      })
                    : FALLBACK_COLOR_OPTIONS.map((c) => {
                        const active = selectedColor === c.color_name;
                        return (
                          <button
                            key={c.color_name}
                            type="button"
                            aria-label={c.color_name}
                            title={c.color_name}
                            onClick={() => setSelectedColor(c.color_name)}
                            style={{ backgroundColor: c.color_hex }}
                            className={`h-8 w-8 rounded-full border border-bone/30 transition-all ${
                              active
                                ? "ring-2 ring-gold ring-offset-2 ring-offset-background"
                                : "hover:scale-110"
                            }`}
                          />
                        );
                      })}
                </div>
              </div>

              <div className="mt-7">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="eyebrow">
                    Size: <span className="text-foreground">{selectedSize}</span>
                  </span>
                  <Link
                    to="/journal/$slug"
                    params={{ slug: "how-to-choose-the-right-size" }}
                    className="text-[10px] uppercase tracking-[0.24em] text-gold inline-flex items-center gap-1.5 hover:underline underline-offset-4"
                  >
                    <Ruler className="h-3 w-3" /> Size Guide
                  </Link>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {SIZES.map((s) => {
                    const active = selectedSize === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSelectedSize(s)}
                        className={`py-2.5 text-xs tracking-wider border transition-colors ${
                          active
                            ? "border-gold bg-gold/10 text-foreground"
                            : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  // Inventory counts are intentionally not exposed publicly.
                  // Show a neutral availability indicator only.
                  return (
                    <p className="mt-3 text-xs text-foreground/70 inline-flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-gold" />
                      In-stock and ready to ship
                    </p>
                  );
                })()}
              </div>

              <div className="mt-7 flex items-stretch gap-3">
                <div className="flex items-center border border-border">
                  <button
                    aria-label="Decrease quantity"
                    className="px-3 h-full hover:bg-secondary"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-10 text-center text-sm tabular-nums">{qty}</span>
                  <button
                    aria-label="Increase quantity"
                    className="px-3 h-full hover:bg-secondary"
                    onClick={() => setQty((q) => q + 1)}
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>

                <button
                  onClick={onAdd}
                  className="btn-luxe flex-1 bg-ink text-bone py-4 text-[11px] uppercase tracking-[0.32em] gap-2 text-black bg-slate-50 items-center justify-end flex flex-col"
                >
                  {added ? (
                    <>
                      <Check className="h-4 w-4" /> Added to Bag
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="h-4 w-4" /> Add to Bag
                    </>
                  )}
                </button>

                <button
                  type="button"
                  aria-label={wished ? "Remove from wishlist" : "Save to wishlist"}
                  onClick={() => void toggle(product.slug)}
                  className="border border-ink/20 px-4 hover:border-ink transition-colors"
                >
                  <Heart className={`h-4 w-4 ${wished ? "fill-ink text-ink" : "text-ink"}`} />
                </button>
              </div>

              <div className="mt-6 flex items-center justify-center gap-2 py-3 border-t border-b border-ink/10">
                <ShieldCheck className="h-4 w-4 text-ink" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-ink">
                  100% Real Leather Guarantee
                </span>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                {[
                  { icon: ShieldCheck, label: "Genuine Leather Guarantee" },
                  { icon: RotateCcw, label: "Free Returns" },
                  { icon: Truck, label: "Worldwide Shipping" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-center gap-2">
                    <Icon className="h-4 w-4 text-gold" />
                    <span className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground leading-tight">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Accordion title="Product Details" defaultOpen>
                  <p className="mb-4">
                    {product.description}
                    {product.leather_type ? ` Crafted in ${product.leather_type}.` : ""}
                  </p>
                  {product.details.length > 0 && (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.24em] text-foreground/90 mb-2">
                        Specifications
                      </p>
                      <ul className="space-y-1.5">
                        {product.details.map((d: string) => (
                          <li key={d} className="flex gap-3">
                            <span className="text-gold">—</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Accordion>

                {product.story && (
                  <Accordion title="The Story">
                    <p className="italic font-serif leading-loose">{product.story}</p>
                  </Accordion>
                )}

                <Accordion title="Care Details">
                  <ul className="space-y-2">
                    <li>— Wipe gently with a soft, dry cloth after wear.</li>
                    <li>— Condition every 3–6 months with a neutral leather balm.</li>
                    <li>— Store on a wide hanger, away from direct sunlight and humidity.</li>
                    <li>— Avoid prolonged contact with rain; blot — never rub — if wet.</li>
                    <li>— Professional leather cleaning only.</li>
                  </ul>
                </Accordion>

                <Accordion title="Shipping & Returns">
                  <p className="mb-2">
                    Complimentary worldwide shipping on every order. Express delivery within 3–5
                    business days, fully tracked and insured.
                  </p>
                  <p>
                    Not the right fit? Return any unworn piece within 30 days for a full refund or
                    free exchange.
                  </p>
                </Accordion>

                <div className="border-t border-border py-5">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                    SKU: <span className="text-foreground">{sku}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-border p-3 flex gap-3">
        <div className="flex-1 flex items-center justify-between px-2">
          <span className="font-serif text-base truncate">{product.name}</span>
          <span className="text-sm tabular-nums text-ink">{formatPrice(product.price * qty)}</span>
        </div>
        <button
          onClick={onAdd}
          className="bg-foreground text-background px-5 py-3 text-[11px] uppercase tracking-[0.24em]"
        >
          {added ? "Added" : "Add"}
        </button>
      </div>

      {/* EDITORIAL STORYTELLING */}
      <section className="bg-bone">
        <div className="container-luxe py-28 md:py-40">
          <div className="grid md:grid-cols-2 gap-14 md:gap-24 items-center">
            <div>
              <div className="eyebrow mb-6">The Story</div>
              <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.1] tracking-[-0.01em] text-ink">
                A piece chosen
                <br />
                <span className="italic">to outlast trends.</span>
              </h2>
              <p className="mt-8 text-base text-ash leading-[1.85] font-light max-w-md">
                {product.story ??
                  "Cut from full-grain hides selected one at a time, this piece is finished entirely in the KAKAIYS atelier. The leather is soft to the hand on the first day, and richer with every season. It is meant to be worn, marked, and carried into the years."}
              </p>
              <ul className="mt-12 space-y-6">
                {[
                  {
                    icon: Hand,
                    title: "Hand-finished in Sialkot",
                    body: "Edges, hardware and lining set by one artisan, by name.",
                  },
                  {
                    icon: Leaf,
                    title: "Full-grain, traceable hides",
                    body: "From two heritage tanneries we have visited for years.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Lifetime promise",
                    body: "Repaired, reconditioned and restored — for as long as you wear it.",
                  },
                ].map(({ icon: Icon, title, body }) => (
                  <li key={title} className="flex gap-5">
                    <Icon className="h-4 w-4 text-ink shrink-0 mt-1" />
                    <div>
                      <p className="text-sm text-ink">{title}</p>
                      <p className="text-sm text-ash mt-1 leading-relaxed font-light">
                        {body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative aspect-[4/5] overflow-hidden img-frame">
              <img
                src={editorialCouple}
                alt="A couple in KAKAIYS leather captured in golden hour Parisian light"
                loading="lazy"
                width={1080}
                height={1350}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MATERIAL / DETAIL GALLERY — alternating editorial */}
      <section className="bg-cream">
        <div className="container-luxe py-28 md:py-40">
          <div className="text-center max-w-2xl mx-auto mb-16 md:mb-24">
            <div className="eyebrow mb-5">In Detail</div>
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.1] tracking-[-0.01em]">
              The marks of the hand.
            </h2>
          </div>

          <div className="space-y-20 md:space-y-32">
            <div className="grid md:grid-cols-12 gap-8 md:gap-14 items-center">
              <div className="md:col-span-7 relative aspect-[5/4] overflow-hidden img-frame">
                <img
                  src={leatherCloseup}
                  alt="Macro detail of full-grain leather"
                  loading="lazy"
                  width={1400}
                  height={1120}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="md:col-span-5 md:pl-6">
                <div className="eyebrow mb-4">The Hide</div>
                <h3 className="font-serif font-light text-2xl md:text-3xl text-ink leading-[1.2]">
                  Full-grain, unaltered.
                </h3>
                <p className="mt-5 text-sm text-ash leading-[1.85] font-light">
                  We work only with the top layer of the hide — the part that
                  carries the natural grain, the small scars, the irregularities
                  that announce a piece is real.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-12 gap-8 md:gap-14 items-center">
              <div className="md:col-span-5 md:order-2">
                <div className="eyebrow mb-4">The Stitch</div>
                <h3 className="font-serif font-light text-2xl md:text-3xl text-ink leading-[1.2]">
                  Saddle-stitched, by hand.
                </h3>
                <p className="mt-5 text-sm text-ash leading-[1.85] font-light">
                  Two needles, one thread, drawn through every hole the artisan
                  has pierced. It is slow, and it does not unravel — even if a
                  stitch is cut.
                </p>
              </div>
              <div className="md:col-span-7 md:order-1 relative aspect-[5/4] overflow-hidden img-frame">
                <img
                  src={editorialCouple}
                  alt="Hand-stitch detail on KAKAIYS leather"
                  loading="lazy"
                  width={1400}
                  height={1120}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProductReviews productSlug={product.slug} />

      {/* RELATED — curated, larger tiles */}
      {related.length > 0 && (
        <section className="bg-bone">
          <div className="container-luxe py-28 md:py-40 mb-24 lg:mb-0">
            <div className="text-center mb-16 md:mb-20">
              <div className="eyebrow mb-5">Also in {product.category}</div>
              <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.1] tracking-[-0.01em]">
                You may also love.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-20">
              {related.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
